import * as styles from "./TimeRangePickerOverview.css";

import { clsx } from "clsx";
import { useRef, useState } from "react";
import type { JSX, KeyboardEvent, PointerEvent } from "react";

import { useLingui } from "@lingui/react/macro";

import { useTimeRangePickerContext } from "./TimeRangePicker.context";
import type { TimeRangePickerOverviewProps } from "./TimeRangePicker.types";
import { clamp, formatRange, snap } from "./timeMath";
import { useZoomGestures } from "./useZoomGestures";

type DragMode = "pan" | "begin" | "end";

type Drag = {
  pointerId: number;
  mode: DragMode;
  grab: number;
};

/**
 * The whole day at a glance, under the track, with the slice the track shows
 * drawn as a window. Dragging the window pans the track, dragging an edge
 * zooms it, and pressing the strip jumps the window there.
 */
export function TimeRangePickerOverview({
  className,
  ...aria
}: TimeRangePickerOverviewProps): JSX.Element {
  const { t } = useLingui();

  const {
    ranges,
    min,
    max,
    view,
    minView,
    step,
    disabled,
    setView,
    zoomAt,
    panBy,
  } = useTimeRangePickerContext();

  const overviewRef = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState(false);

  const span = view.end - view.begin;

  useZoomGestures(overviewRef, {
    enabled: !disabled,
    span,
    zoomAt,
    panBy,
    anchorAt: (clientX) => {
      const rect = overviewRef.current?.getBoundingClientRect();

      if (!rect || 0 === rect.width) {
        return view.begin;
      }

      return min + ((clientX - rect.left) / rect.width) * (max - min);
    },
  });

  const percentOf = (minutes: number) => ((minutes - min) / (max - min)) * 100;

  const minutesAt = (clientX: number) => {
    const rect = overviewRef.current?.getBoundingClientRect();

    if (!rect || 0 === rect.width) {
      return min;
    }

    const ratio = (clientX - rect.left) / rect.width;

    return clamp(snap(min + ratio * (max - min), step), min, max);
  };

  const panTo = (begin: number) => {
    const next = clamp(begin, min, max - span);

    setView({ begin: next, end: next + span });
  };

  // The pointer is captured on the strip, whichever part started the drag,
  // so one pair of move handlers serves the window and its two edges.
  const startDrag = (
    event: PointerEvent<HTMLElement>,
    mode: DragMode,
    grab: number,
  ) => {
    if (disabled || 0 !== event.button) {
      return;
    }

    event.stopPropagation();
    overviewRef.current?.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, mode, grab };
    setDragging(true);
  };

  const handleBackgroundPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (
      event.target !== event.currentTarget ||
      disabled ||
      0 !== event.button
    ) {
      return;
    }

    panTo(minutesAt(event.clientX) - span / 2);
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, mode: "pan", grab: span / 2 };
    setDragging(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;

    if (!current || current.pointerId !== event.pointerId) {
      return;
    }

    const cursor = minutesAt(event.clientX);

    if ("pan" === current.mode) {
      panTo(cursor - current.grab);
    } else if ("begin" === current.mode) {
      setView({
        begin: clamp(cursor, min, view.end - minView),
        end: view.end,
      });
    } else {
      setView({
        begin: view.begin,
        end: clamp(cursor, view.begin + minView, max),
      });
    }
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;

    if (!current || current.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    drag.current = null;
    setDragging(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    const nudge = Math.max(step, snap(span / 10, step));

    const direction =
      "ArrowLeft" === event.key ? -1 : "ArrowRight" === event.key ? 1 : 0;

    if (0 !== direction) {
      event.preventDefault();

      if (event.shiftKey) {
        const next = clamp(span + direction * nudge, minView, max - min);
        const centre = view.begin + span / 2;
        const begin = clamp(centre - next / 2, min, max - next);

        setView({ begin, end: begin + next });
      } else {
        panTo(view.begin + direction * nudge);
      }

      return;
    }

    if ("Home" === event.key) {
      event.preventDefault();
      panTo(min);
    } else if ("End" === event.key) {
      event.preventDefault();
      panTo(max - span);
    } else if ("Escape" === event.key) {
      event.preventDefault();
      setView({ begin: min, end: max });
    }
  };

  const left = percentOf(view.begin);
  const right = percentOf(view.end);

  return (
    <div
      ref={overviewRef}
      data-slot="time-range-picker-overview"
      data-disabled={disabled || undefined}
      className={clsx(styles.overview, className)}
      onPointerDown={handleBackgroundPointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {ranges.map((range, index) =>
        null === range ? null : (
          <div
            key={index}
            className={styles.range}
            style={{
              left: `${percentOf(range.begin)}%`,
              width: `${percentOf(range.end) - percentOf(range.begin)}%`,
            }}
          />
        ),
      )}

      <div className={styles.shade} style={{ left: 0, width: `${left}%` }} />
      <div className={styles.shade} style={{ left: `${right}%`, right: 0 }} />

      <div
        className={styles.viewport}
        style={{ left: `${left}%`, width: `${right - left}%` }}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={aria["aria-label"] ?? t`Visible range`}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={view.begin}
        aria-valuetext={formatRange(view)}
        aria-disabled={disabled || undefined}
        data-dragging={dragging || undefined}
        onPointerDown={(event) =>
          startDrag(event, "pan", minutesAt(event.clientX) - view.begin)
        }
        onKeyDown={handleKeyDown}
      >
        <div
          className={styles.viewportHandle}
          data-side="begin"
          onPointerDown={(event) => startDrag(event, "begin", 0)}
        />

        <div
          className={styles.viewportHandle}
          data-side="end"
          onPointerDown={(event) => startDrag(event, "end", 0)}
        />
      </div>
    </div>
  );
}
